import React, { useEffect, useState } from 'react'
import request from '@/modules/base/util/request'
import PropTypes from 'prop-types'
import Topbar from '@/modules/base/components/topbar/topbar'
import lang from '@/lang'
import { makeStyles, useTheme } from '@mui/styles'
import cleverreachConfig from '@/modules/apps/own/cleverreach/config'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconArrow from '@assets/images/arrow-up.svg'
import DuplicatedCustomerEmails from './duplicatedCustomerEmails'
import WorkInProgress from './workInProgress'
import InitialView from './initialView'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { navigate } from '@/modules/layout/util/useInterceptedNavigate'
import { withAsyncData } from '@/utils/withAsyncData'
import appsConfig from '@/modules/apps/config'

const { cleverreach: cleverreachLang } = lang

const useStyles = makeStyles((theme) => ({
  wrapper: {
    height: `calc(100vh - ${theme.menuHeight}px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    height: `calc(100vh - ${theme.pageContentOffset}px)`,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    textAlign: 'center',
    width: '100%',
    maxWidth: 730,
    margin: '0 auto',
  },
  stepsContainer: {
    marginTop: 110,
    flex: 1,
    marginBottom: 10,
  },
  footer: {
    height: 85,
    border: `1px solid ${theme.backgroundColor}`,
    flexShrink: 0,
    backgroundColor: theme.backgroundColor,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  contentWrapper: {
    flex: 1,
  },
  footerContent: {
    justifySelf: 'flex-end',
    height: '100%',
    width: 285,
    backgroundColor: theme.primary,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.white,
    letterSpacing: '0.28px',
    padding: '0 40px',
    cursor: 'pointer',
  },
  footerTextAndIcon: {
    display: 'flex',
  },
  arrowIcon: {
    marginLeft: 10,
    transform: 'rotate(90deg)',
  },
}))

const CleverreachWrapper = (props) => {
  const { syncDetails: prefetchedData, cleverreach = false } = props
  const theme = useTheme()
  const redirectToCleverreachLogin = () => {
    const { clientId } = syncDetails
    const queryString = [
      `?client_id=${clientId}`,
      'grant=basic',
      'response_type=code',
      `redirect_uri=${location.origin}/cleverreach`,
    ].join('&')
    location.replace(`${cleverreachConfig.externalUrls.cleverreachLoginBaseUrl}${queryString}`)
  }
  const [syncDetails, setSyncDetails] = useState(prefetchedData)
  const [toggle, setToggle] = useState(false)
  const syncCleverreach = () => {
    request(cleverreachConfig.endpoints.sync, {
      auth: true,
      method: 'POST',
    })
      .then(() => {
        setStep({ stepNumber: 3, buttonText: cleverreachLang.buttonTextDashboard, buttonAction: navigateToDashBoard, subTitle: cleverreachLang.buttonTextDashboard })
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
      })
  }
  const [step, setStep] = useState(
    cleverreach
      ? {
        stepNumber: 1, buttonText: cleverreachLang.buttonTextTransmit, buttonAction: syncCleverreach, subTitle: `${cleverreachLang.connectionSuccessful} ${cleverreachLang.subTitleTransmit}`,
      }
      : {
        stepNumber: 0,
        buttonText: cleverreachLang.buttonTextStart,
        buttonAction: null,
        subTitle: cleverreachLang.mainSubtitle,
      },
  )

  const classes = useStyles()

  useEffect(() => {
    getSettings()
  }, [toggle])

  const onTransferFailed = () => {
    setStep({ stepNumber: 1, buttonText: cleverreachLang.buttonTextConnectToCleverreach, buttonAction: redirectToCleverreachLogin, subTitle: cleverreachLang.connectToCleverreach })
  }
  const getSettings = () => {
    request(cleverreachConfig.endpoints.settings, { auth: true }).then(({ body: data }) => {
      setSyncDetails({ ...data })
      if (toggle) {
        if (data.active && syncDetails.duplicatedEmails === 0) {
          setStep({ stepNumber: 1, buttonText: cleverreachLang.buttonTextTransmit, buttonAction: syncCleverreach, subTitle: cleverreachLang.subTitleTransmit })
        } else if (data.active) {
          setStep({ stepNumber: 2, buttonText: cleverreachLang.buttonTextTransmitWithDuplicates, buttonAction: syncCleverreach, subTitle: '' })
        } else {
          setStep({ stepNumber: 1, buttonText: cleverreachLang.buttonTextConnectToCleverreach, buttonAction: redirectToCleverreachLogin, subTitle: cleverreachLang.connectToCleverreach })
        }
      }
    })
  }

  const navigateToDashBoard = () => {
    navigate('/dashboard')
  }

  const renderView = () => {
    switch (step.stepNumber) {
      case 0:
        return <InitialView syncDetails={syncDetails} subTitle={step.subTitle} />
      case 1:
        return <InitialView syncDetails={syncDetails} subTitle={step.subTitle} />
      case 2:
        return <DuplicatedCustomerEmails syncDetails={syncDetails} />
      case 3:
        return <WorkInProgress onTransferFailed={onTransferFailed} />
      default:
        return <InitialView syncDetails={syncDetails} subTitle={step.subTitle} />
    }
  }

  return (
    <div className={classes.wrapper}>
      <Topbar
        backButtonTitle={cleverreachLang.topbarTitle}
        backButtonUrl={'/apps'}
        variant='white'
      />
      <div className={classes.contentWrapper}>
        <div className={classes.main}>
          <div className={classes.stepsContainer}>
            {renderView()}
          </div>
        </div>
        {step.stepNumber !== 3
        && <footer className={classes.footer}>
          <div className={ classes.footerContent } onClick={() => {
            if (step.stepNumber > 0) {
              step.buttonAction()
            } else {
              setToggle(true)
            }
          }}>
            <div className={classes.footerTextAndIcon}>
              <span>{step.buttonText}</span>
              <SVGIcon
                className={classes.arrowIcon}
                svg={IconArrow}
                fill={theme.white}
                width={'15px'}
                height={'15px'}
              />
            </div>
          </div>
        </footer>
        }
      </div>
    </div>
  )
}

CleverreachWrapper.propTypes = {
  cleverreach: PropTypes.bool,
}

const handleCleverReachToken = (code) => {
  return request(`${cleverreachConfig.endpoints.token}`, {
    method: 'POST',
    auth: true,
    isConcurrent: true,
    data: { code },
  })
    .then(() => {
      request(`${appsConfig.api.apps}?filter=myApps&searchText=cleverreach`, { auth: true })
        .then(({ body: { data } }) => {
          const appId = data.filter((app) => app.customData.isInternalApp)[0].appId
          navigate(`/cleverreach/${appId}?cleverreach=true`)
        })
    })
    .catch(error => {
      console.error({ error })
    })
}

const handleCleverReachAppId = (appId) => {
  return request(cleverreachConfig.endpoints.settings, { auth: true, isConcurrent: true }).then(({ body: syncDetails }) => {
    return Promise.resolve({ syncDetails })
  }).catch(() => {
    toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
  })
}

const fetchData = async({ params, searchParams }) => {
  const { code } = searchParams
  const { appId } = params

  if (code) {
    await handleCleverReachToken(code)
  } else if (appId) {
    const syncDetails = await handleCleverReachAppId(appId)
    return Promise.resolve({ appId, syncDetails })
  }
}

export default withAsyncData(CleverreachWrapper, lang.menu.apps.title, fetchData)
